// Что уходит наружу — жёсткий whitelist (§10.2 платформы, §8 федерации).
//
// Правило одно и оно несимметрично входящему контракту: ответы партнёра
// разбираются `.passthrough()` (он деплоится без нас), а всё исходящее собирается
// `.strict()` через ЕДИНСТВЕННУЮ функцию `buildOutboundContext`. Причина: утечка
// наружу необратима. Поле, добавленное в объект «просто чтобы было», уедет к
// партнёру в первом же релизе и обратно его не вернуть.
//
// Три инварианта, которые здесь закодированы:
//  1. **Никаких наших id.** `materialId` / `chunkId` / имена файлов не покидают
//     сервер. Наружу уходят непрозрачные `s1..sN`, сопоставление хранится у нас
//     (`sourceMap`) — цитаты в UI строятся локально. Это и приватность, и защита
//     от того, что партнёр начнёт зависеть от наших внутренних идентификаторов.
//  2. **Никаких PII.** userId, email, имя, аватар в схеме отсутствуют физически:
//     `.strict()` не даст их «случайно» дописать, а тест `outbound.test.ts`
//     падает при появлении нового ключа в схеме без осознанного решения.
//  3. **Ограниченный объём.** Сниппеты режутся по символам и по количеству;
//     обрезка — громкая (`dropped`), потому что молча урезанный контекст
//     выглядит как «модель тупит», а не как сработавший лимит.
import { z } from "zod";

// ─── Лимиты объёма ──────────────────────────────────────────────────────────

/** Максимум сниппетов в одном turn. */
export const OUTBOUND_SOURCES_MAX = 12;
/** Максимум символов в одном сниппете. */
export const OUTBOUND_SNIPPET_MAX_CHARS = 1_200;
/** Суммарный потолок сниппетов на turn (§8 федерации). */
export const OUTBOUND_TOTAL_CHARS_MAX = 8_000;
/** Потолок сообщения пользователя — проверяется ДО отправки наружу (§15a.2). */
export const OUTBOUND_MESSAGE_MAX_CHARS = 4_000;
/** Потолок педагогической инструкции. */
export const OUTBOUND_INSTRUCTIONS_MAX_CHARS = 4_000;

/** Учебные сценарии — константы кода, не таблица (§15 федерации). */
export const OUTBOUND_SCENARIOS = ["exam-prep", "socratic", "material-explain", "free-kb"] as const;
export type OutboundScenario = (typeof OUTBOUND_SCENARIOS)[number];

// ─── Схема исходящего контекста ─────────────────────────────────────────────

export const outboundSourceSchema = z
	.object({
		/** Непрозрачный ref вида `s1`. Внутренние id сюда не попадают никогда. */
		id: z.string().regex(/^s\d+$/),
		snippet: z.string().min(1).max(OUTBOUND_SNIPPET_MAX_CHARS),
		page: z.number().int().positive().optional(),
	})
	.strict();

export const outboundLearningContextSchema = z
	.object({
		scenarioId: z.enum(OUTBOUND_SCENARIOS),
		studyInstructions: z.string().max(OUTBOUND_INSTRUCTIONS_MAX_CHARS),
		sources: z.array(outboundSourceSchema).max(OUTBOUND_SOURCES_MAX),
		requireCitations: z.literal(true),
		/** ISO 639-1 — локаль ответа. */
		locale: z.string().min(2).max(8),
		safety: z
			.object({
				studyMode: z.literal(true),
				maxIntensity: z.literal("edu"),
			})
			.strict(),
	})
	.strict();

export type OutboundLearningContext = z.infer<typeof outboundLearningContextSchema>;

/**
 * Полный список ключей исходящего контекста. Дублирует схему СОЗНАТЕЛЬНО: тест
 * сверяет два независимых объявления и падает, когда в схему добавили поле, а в
 * этот список — нет. Дешёвая имитация ревью там, где «просто добавил поле»
 * означает утечку.
 */
export const OUTBOUND_CONTEXT_KEYS = [
	"scenarioId",
	"studyInstructions",
	"sources",
	"requireCitations",
	"locale",
	"safety",
] as const;

// ─── Сборка ─────────────────────────────────────────────────────────────────

/** Внутренний источник — то, что живёт у нас и наружу в таком виде не уходит. */
export interface InternalSource {
	materialId: string;
	chunkId: string;
	snippet: string;
	page?: number;
	title?: string;
}

export interface OutboundBuildInput {
	scenarioId: OutboundScenario;
	studyInstructions: string;
	locale: string;
	sources: readonly InternalSource[];
}

export interface OutboundBuildResult {
	context: OutboundLearningContext;
	/** `s1 → {materialId, chunkId}`. Остаётся на сервере, наружу не уходит. */
	sourceMap: Record<string, { materialId: string; chunkId: string; page?: number }>;
	/** Что урезано лимитами — вызывающий обязан это залогировать (`degradation`). */
	dropped: {
		sourcesDroppedByCount: number;
		sourcesDroppedByTotalChars: number;
		snippetsTruncated: number;
		instructionsTruncated: boolean;
	};
}

/**
 * Единственный способ собрать исходящий контекст.
 *
 * Возвращает не только объект, но и `sourceMap` (сопоставление остаётся у нас) и
 * `dropped` — фильтр, способный обнулить результат, обязан кричать, а не молча
 * отдавать пустой список (правило громкой деградации, `rules/logging.md` §13a).
 */
export function buildOutboundContext(input: OutboundBuildInput): OutboundBuildResult {
	const dropped: OutboundBuildResult["dropped"] = {
		sourcesDroppedByCount: 0,
		sourcesDroppedByTotalChars: 0,
		snippetsTruncated: 0,
		instructionsTruncated: false,
	};

	const withinCount = input.sources.slice(0, OUTBOUND_SOURCES_MAX);
	dropped.sourcesDroppedByCount = input.sources.length - withinCount.length;

	const sources: Array<z.infer<typeof outboundSourceSchema>> = [];
	const sourceMap: OutboundBuildResult["sourceMap"] = {};
	let totalChars = 0;

	for (const source of withinCount) {
		const normalized = source.snippet.trim();
		if (!normalized) continue;
		let snippet = normalized;
		if (snippet.length > OUTBOUND_SNIPPET_MAX_CHARS) {
			snippet = snippet.slice(0, OUTBOUND_SNIPPET_MAX_CHARS);
			dropped.snippetsTruncated += 1;
		}
		if (totalChars + snippet.length > OUTBOUND_TOTAL_CHARS_MAX) {
			dropped.sourcesDroppedByTotalChars += 1;
			continue;
		}
		totalChars += snippet.length;
		const ref = `s${sources.length + 1}`;
		sources.push({ id: ref, snippet, ...(source.page ? { page: source.page } : {}) });
		sourceMap[ref] = {
			materialId: source.materialId,
			chunkId: source.chunkId,
			...(source.page ? { page: source.page } : {}),
		};
	}

	let studyInstructions = input.studyInstructions;
	if (studyInstructions.length > OUTBOUND_INSTRUCTIONS_MAX_CHARS) {
		studyInstructions = studyInstructions.slice(0, OUTBOUND_INSTRUCTIONS_MAX_CHARS);
		dropped.instructionsTruncated = true;
	}

	// Прогон через `.strict()`-схему — не формальность: он же и есть гарантия,
	// что структура не расширилась мимо whitelist'а.
	const context = outboundLearningContextSchema.parse({
		scenarioId: input.scenarioId,
		studyInstructions,
		sources,
		requireCitations: true,
		locale: input.locale,
		safety: { studyMode: true, maxIntensity: "edu" },
	});

	return { context, sourceMap, dropped };
}

/** Урезано ли что-нибудь — удобный предикат для решения «логировать warn или нет». */
export function hasOutboundDrops(dropped: OutboundBuildResult["dropped"]): boolean {
	return (
		dropped.sourcesDroppedByCount > 0 ||
		dropped.sourcesDroppedByTotalChars > 0 ||
		dropped.snippetsTruncated > 0 ||
		dropped.instructionsTruncated
	);
}
